import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { getTeacherAssignments, getStudentAssignments } from '../../services/assignmentService';
import halaqaService from '../../services/halaqaService';
import AuthContext from '../../contexts/AuthContext';
import api from '../../api/api';
import { isToday, isPast } from '../../utils/dateUtils';
import { getAssignmentTitle } from '../../utils/assignmentUtils';
import '../../styles/AssignmentsListPage.css';

/**
 * AssignmentsListPage - Assignment management view for TEACHERS
 * 
 * Used by:
 * - Teachers: Managing assignments for their classes/students
 * 
 * Features:
 * - Create, edit, delete assignments
 * - Filter by class (halaqa) and student
 * - Shows assignments sorted newest first
 * - Includes action buttons (Edit/View)
 * 
 * Different from StudentAssignmentsListPage which is used by STUDENTS/PARENTS for viewing only
 */
const AssignmentsListPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [teacherHalaqa, setTeacherHalaqa] = useState(null);
  // Removed displayLimit - show all assignments for better UX with typical 4-5 assignments per week
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [localStudentName, setLocalStudentName] = useState('');
  
  // Get studentId from URL params first, then fall back to location state
  const studentId = params.studentId || location.state?.studentId;
  const studentName = location.state?.studentName;
  
  // Get class context from location state for filtering (when navigating from class context)
  const contextHalaqaId = location.state?.halaqaId;
  const contextHalaqaName = location.state?.halaqaName;

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      
      let data;
      
      // If student role, get their own assignments
      if (user?.role === 'student') {
        // Get the logged-in student's assignments
        data = await getStudentAssignments();
        console.log('Fetched assignments for logged-in student:', data);
      }
      // If we have a studentId from params (teacher viewing student), use the student-specific endpoint
      else if (studentId) {
        // Pass halaqaId parameter if we have class context for filtering
        data = await getStudentAssignments(studentId, contextHalaqaId);
        console.log(`Fetched assignments for student ${studentId}${contextHalaqaId ? ` in class ${contextHalaqaName}` : ''}:`, data);
      } else {
        // Otherwise, get all teacher assignments
        data = await getTeacherAssignments(
          user?.center, 
          teacherHalaqa ? teacherHalaqa._id : null
        );
        console.log('Fetched teacher assignments:', data);
      }
      
      // Ensure data is an array
      if (Array.isArray(data)) {
        setAssignments(data);
      } else if (data && Array.isArray(data.data)) {
        // Handle potential API response structure
        setAssignments(data.data);
      } else {
        console.error('Expected array of assignments but got:', data);
        setAssignments([]);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setError('Failed to load assignments. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeacherHalaqa = async () => {
    try {
      const halaqas = await halaqaService.getAssignedHalaqas();
      if (halaqas && halaqas.length > 0) {
        setTeacherHalaqa(halaqas[0]);
      }
    } catch (error) {
      console.error('Error fetching teacher halaqa:', error);
    }
  };

  useEffect(() => {
    // Call both functions
    fetchAssignments();
    fetchTeacherHalaqa();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Separate effect for fetching student name to avoid dependency issues
  useEffect(() => {
    // If we're viewing a specific student's assignments but don't have the name
    if (studentId && !studentName) {
      const fetchStudentName = async () => {
        try {
          // Try to get the student name from the API
          const response = await api.get(`/users/${studentId}`);
          if (response.data && response.data.name) {
            // Store it in local state since we can't update location.state
            setLocalStudentName(response.data.name);
          }
        } catch (error) {
          console.error('Error fetching student name:', error);
        }
      };
      
      fetchStudentName();
    }
  }, [studentId, studentName]);
  
  // Handle scroll events to show/hide scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Function to scroll back to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };


  const filterAssignments = (filter) => {
    setActiveFilter(filter);
  };

  // Determine urgency bucket for an assignment
  const getUrgencyBucket = (assignment) => {
    const dueDate = new Date(assignment.dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const weekFromNow = new Date(today);
    weekFromNow.setDate(weekFromNow.getDate() + 7);

    // Overdue
    if (dueDate < today) return 1;

    // Due today
    if (dueDate >= today && dueDate < tomorrow) return 2;

    // Due soon (next 7 days)
    if (dueDate >= tomorrow && dueDate < weekFromNow) return 3;

    // Future (7+ days away)
    return 4;
  };

  const getFilteredAssignments = () => {
    let filtered;

    // Apply filter
    if (activeFilter === 'all') {
      filtered = [...assignments]; // Create a copy to avoid mutating state
    } else if (activeFilter === 'pending') {
      filtered = assignments.filter(assignment =>
        assignment.status === 'active' &&
        (!assignment.assessment || !assignment.assessment.rating)
      );
    } else if (activeFilter === 'assessed') {
      filtered = assignments.filter(assignment =>
        assignment.assessment && assignment.assessment.rating
      );
    } else if (activeFilter === 'new-memorization' || activeFilter === 'revision' || activeFilter === 'recitation') {
      filtered = assignments.filter(assignment =>
        assignment.type === activeFilter
      );
    } else {
      filtered = [...assignments]; // Fallback
    }

    // TRANSITION TODO: Remove type-specific sorting once fully migrated to plan-based assignments
    // Two-phase sorting:
    // Phase 1: Group by urgency (Overdue → Due Today → Due Soon → Future)
    // Phase 2: Within each bucket, sort by type-specific rules:
    //   - Plan-based: chronological (earliest first)
    //   - One-off: most recent first (latest due date first)
    return filtered.sort((a, b) => {
      const bucketA = getUrgencyBucket(a);
      const bucketB = getUrgencyBucket(b);

      // Sort by urgency bucket first
      if (bucketA !== bucketB) {
        return bucketA - bucketB;
      }

      // Within same bucket, apply type-specific sorting
      const dateA = new Date(a.dueDate);
      const dateB = new Date(b.dueDate);

      // Both plan-based: earliest first (ascending)
      if (a.planId && b.planId) {
        return dateA - dateB;
      }

      // Both one-off: most recent first (descending)
      if (!a.planId && !b.planId) {
        return dateB - dateA;
      }

      // Mixed (one plan, one one-off): plan-based first
      return a.planId ? -1 : 1;
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const getPlanBadgeColors = (type) => {
    switch(type) {
      case 'revision':
        return { backgroundColor: '#f3e5f5', color: '#7227b0' };
      case 'recitation':
        return { backgroundColor: '#e8f5e9', color: '#1b5e20' };
      case 'new-memorization':
      default:
        return { backgroundColor: '#fef8e8', color: '#f57c00' };
    }
  };

  const renderAssignmentCard = (item) => {
    // Basic validation for assignment object
    if (!item) {
      console.error('Invalid assignment data:', item);
      return null;
    }

    const isAssessed = item.assessment && item.assessment.rating;
    
    // Ultra basic card layout that mirrors what's shown in Revision tab
    return (
      <div className="simple-assignment-card" key={item._id}>
        <div className="simple-assignment-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h3 className="simple-assignment-title">
              {getAssignmentTitle(item)}
            </h3>
          </div>
          <div className="simple-action-buttons">
            <button
              onClick={(e) => {
                e.preventDefault();
                navigate(`/assignments/${item._id}/edit`, {
                  state: { isEditMode: true, assignmentId: item._id }
                });
              }}
              className="simple-edit-btn">
              Edit
            </button>

            <button
              onClick={(e) => {
                e.preventDefault();
                if (item.students && item.students.length > 0) {
                  navigate(`/assignments/${item._id}/assess`, {
                    state: {
                      studentId: item.students[0]._id,
                      studentName: item.students[0].name,
                      assignmentDetails: getAssignmentTitle(item)
                    }
                  });
                } else {
                  window.alert('No students assigned to this assignment');
                }
              }}
              className="simple-assess-btn">
              {isAssessed ? 'View' : 'Assess'}
            </button>
          </div>
        </div>
        
        <div className="simple-assignment-details">
          {studentId ? 
            // When viewing a specific student's assignments, use the studentName from params or our local state
            `Assigned to: ${studentName || localStudentName || 'Student'}` 
            : 
            // Normal case - display students from the assignment
            `Assigned to: ${Array.isArray(item.students) ? 
              item.students.map(s => typeof s === 'object' ? s.name : s).join(', ') : 'No students'}`
          }
        </div>
        
        <div className="simple-assignment-footer">
          <div className="simple-due-date">
            Due: {formatDate(item.dueDate)}
          </div>
          <div className={`simple-status ${
            item.status === 'active' && isPast(item.dueDate) ? 'overdue' :
            item.status === 'active' && isToday(item.dueDate) ? 'due-today' : ''
          }`}>
            {item.status === 'active' && isPast(item.dueDate) ? 'Overdue' :
             item.status === 'active' && isToday(item.dueDate) ? 'Due Today' :
             item.status === 'active' ? 'Active' : item.status}
          </div>
          {item.planId ? (
            <span style={{
              fontSize: '11px',
              padding: '2px 8px',
              ...getPlanBadgeColors(item.type),
              borderRadius: '4px',
              fontWeight: '500',
              textTransform: 'capitalize',
              whiteSpace: 'nowrap'
            }}>
              📋 {item.type.replace('-', ' ')} Plan
            </span>
          ) : (
            <div className={`simple-type ${item.type === 'revision' ? 'revision' : item.type === 'recitation' ? 'recitation' : ''}`}>
              {item.type === 'new-memorization' ? 'New Memorization' :
               item.type === 'revision' ? 'Revision' :
               item.type === 'recitation' ? 'Recitation' : 'Unknown'}
            </div>
          )}
          {isAssessed && (
            <div className="simple-assessment">
              {item.assessment.rating}
            </div>
          )}
        </div>
      </div>
    );
  };

  const canCreateAssignment = () => {
    return user && (user.role === 'admin' || user.role === 'teacher' || user.role === 'super_admin');
  };


  return (
    <div className="assignments-list-container" id="assignments-container">
      <div className="header">
        <h1>{studentId ? `${studentName || localStudentName || 'Student'}'s Assignments` : 'Assignments'}</h1>
        {contextHalaqaId && contextHalaqaName && (
          <div style={{ 
            fontSize: '14px', 
            color: '#666', 
            marginTop: '4px',
            padding: '4px 8px',
            backgroundColor: '#f0f7ff',
            borderRadius: '4px',
            border: '1px solid #cce7ff',
            display: 'inline-block'
          }}>
            Showing {contextHalaqaName} assignments only
          </div>
        )}
        <div className="header-actions">
          {studentId ? (
            location.state?.fromParentChildView ? (
              <Link to={`/parent/children/${studentId}`} className="back-link">
                ← Back to Child Profile
              </Link>
            ) : (
              <Link 
                to={`/students/${studentId}`} 
                className="back-link"
                state={{ 
                  halaqaId: contextHalaqaId,
                  halaqaName: contextHalaqaName 
                }}
              >
                ← Back to Student Profile
              </Link>
            )
          ) : (
            <Link to="/dashboard" className="back-link">
              ← Back to Dashboard
            </Link>
          )}
   
        
        </div>
      </div>

      <div className="filter-bar">
        <div className="filter-chips">
          <button 
            className={`filter-chip ${activeFilter === 'all' ? 'filter-chip-active' : ''}`}
            onClick={() => filterAssignments('all')}
          >
            All
          </button>
          
          <button 
            className={`filter-chip ${activeFilter === 'pending' ? 'filter-chip-active' : ''}`}
            onClick={() => filterAssignments('pending')}
          >
            Pending
          </button>
          
          <button 
            className={`filter-chip ${activeFilter === 'assessed' ? 'filter-chip-active' : ''}`}
            onClick={() => filterAssignments('assessed')}
          >
            Assessed
          </button>
          
          <button 
            className={`filter-chip ${activeFilter === 'new-memorization' ? 'filter-chip-active' : ''}`}
            onClick={() => filterAssignments('new-memorization')}
            data-filter-type="new-memorization"
          >
            New Memorization
          </button>
          
          <button
            className={`filter-chip ${activeFilter === 'revision' ? 'filter-chip-active' : ''}`}
            onClick={() => filterAssignments('revision')}
            data-filter-type="revision"
          >
            Revision
          </button>

          <button
            className={`filter-chip ${activeFilter === 'recitation' ? 'filter-chip-active' : ''}`}
            onClick={() => filterAssignments('recitation')}
            data-filter-type="recitation"
          >
            Recitation
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading assignments...</div>
        </div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <div className="assignments-list">
          {getFilteredAssignments().length > 0 ? (
            <>
              {/* Show how many assignments were found */}
              <div className="assignments-count">
                Found {getFilteredAssignments().length} assignments
              </div>
              {getFilteredAssignments().map((item) => (
                <div key={item._id} className="assignment-wrapper">
                  {renderAssignmentCard(item)}
                </div>
              ))}
              
              {/* Add an extra spacer at the bottom */}
              <div className="list-end-spacer"></div>
              
              {/* Scroll to top button */}
              {showScrollTop && (
                <button 
                  className="scroll-top-btn" 
                  onClick={scrollToTop}
                  aria-label="Scroll to top"
                >
                  ↑ Top
                </button>
              )}
            </>
          ) : (
            <div className="empty-container">
              <p className="empty-text">No assignments found</p>
              {canCreateAssignment() && (
                <Link 
                  to="/assignments/new" 
                  className="create-assignment-link"
                  state={{
                    halaqaId: contextHalaqaId || (teacherHalaqa ? teacherHalaqa._id : null),
                    halaqaName: contextHalaqaName || (teacherHalaqa ? teacherHalaqa.name : null),
                    studentId: studentId,
                    studentName: studentName || localStudentName
                  }}
                >
                  Create a new assignment
                </Link>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssignmentsListPage;